import { Controller } from '@hotwired/stimulus'
import {
  RealtimeAgent,
  RealtimeSession,
  OpenAIRealtimeWebSocket,
} from '@openai/agents-realtime'
import { WavRecorder, WavStreamPlayer } from 'wavtools'
import { marked } from 'marked'
import { markedHighlight } from 'marked-highlight'
import hljs from 'highlight.js'

// Configure marked to use highlight.js via marked-highlight extension
marked.use(
  markedHighlight({
    langPrefix: 'hljs language-',
    highlight(code, lang) {
      const language = hljs.getLanguage(lang) ? lang : 'plaintext'
      return hljs.highlight(code, { language }).value
    }
  })
)

export default class extends Controller {
  static targets = ['transcript', 'button', 'messageText']

  static values = {
    lessonId: String,
    conversationId: String,
    lessonVersion: String,
    instructionVersion: String,
  }

  // Called automatically when a messageText target is connected
  messageTextTargetConnected(element) {
    // Transform assistant messages to markdown
    if (element.dataset.role === 'assistant') {
      const text = element.textContent.trim()
      element.innerHTML = marked.parse(text)
    }
  }

  async createAndRedirect() {
    // Called from lessons#show - creates dialogue and redirects
    try {
      // Get selected instruction from dropdown
      const instructionSelect = document.getElementById('instruction-select')
      if (!instructionSelect) {
        throw new Error('No instruction selected')
      }
      const instructionId = instructionSelect.value

      this.element.disabled = true
      this.element.innerHTML =
        '<span class="spinner-border spinner-border-sm me-2"></span>Creating session...'

      const response = await fetch('/dialogues', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          lesson_id: this.lessonIdValue,
          instruction_id: instructionId,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to create conversation')
      }

      const data = await response.json()

      // Redirect to the dialogue page
      window.location.href = `/dialogues/${data.id}`
    } catch (error) {
      console.error('Failed to create conversation:', error)
      alert('Failed to create session: ' + error.message)

      this.element.disabled = false
      this.element.innerHTML = '<i class="bi bi-mic-fill"></i> Start Tutoring Session'
    }
  }

  async startSession() {
    // Called from dialogues#show - starts actual voice session
    try {
      // Get button reference once
      this.sessionButton = this.hasButtonTarget ? this.buttonTarget : this.element

      // Disable the button and show loading state
      this.sessionButton.disabled = true
      this.sessionButton.innerHTML =
        '<span class="spinner-border spinner-border-sm me-2"></span>Initializing...'

      // Use existing conversation ID
      this.conversationId = this.conversationIdValue

      // Clear ended_at if resuming an ended session
      await fetch(`/dialogues/${this.conversationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          ended_at: null,
        }),
      })

      // Get ephemeral OpenAI API key
      const tokenResponse = await fetch('/api/openai_tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
      })

      if (!tokenResponse.ok) {
        throw new Error('Failed to get OpenAI token')
      }

      const { api_key } = await tokenResponse.json()

      // Fetch dialogue and lesson data
      const dialogueResponse = await fetch(`/dialogues/${this.conversationId}.json`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!dialogueResponse.ok) {
        throw new Error('Failed to fetch dialogue')
      }

      const dialogueData = await dialogueResponse.json()
      this.lessonData = dialogueData.lesson
      this.instructionData = dialogueData.instruction
      this.previousMessages = dialogueData.messages || []

      // Create the RealtimeAgent with instruction-specific content
      // Include both the instruction (how to teach) and lesson (what to teach)
      // Using XML structure as per OpenAI guidelines for better prompt organization
      const combinedInstructions = `<system_instructions>
${this.instructionData.content}
</system_instructions>

<lesson_content>
<title>${this.lessonData.title}</title>
<content>
${this.lessonData.content}
</content>
</lesson_content>`

      const agent = new RealtimeAgent({
        name: 'Voice Tutor',
        instructions: combinedInstructions,
        voice: 'alloy',
        model: 'gpt-realtime',
      })

      // Create WebSocket transport
      // Using ephemeral client secrets that expire in 60 seconds
      const transport = new OpenAIRealtimeWebSocket()

      // Create session with the configured transport
      // Enable input audio transcription in the initial config
      this.session = new RealtimeSession(agent, {
        transport: transport,
        config: {
          input_audio_transcription: {
            model: 'whisper-1',
          },
        },
      })

      // Initialize wavtools for audio handling
      this.recorder = new WavRecorder({ sampleRate: 24000 })
      this.player = new WavStreamPlayer({ sampleRate: 24000 })

      // Listen for events
      this.setupEventListeners()

      // Connect player first
      await this.player.connect()

      // Connect to OpenAI Realtime API
      await this.session.connect({
        apiKey: api_key,
      })

      // Restore conversation history with previous messages when resuming
      if (this.previousMessages.length > 0) {
        // Track restored items to prevent displaying them in UI
        this.restoredItemIds = new Set()

        // Convert previous messages to RealtimeItem format
        const historyItems = this.previousMessages.map((msg, index) => {
          const isUser = msg.role === 'user'
          const itemId = `restored_${index}_${msg.role}`

          // Track this as a restored item
          this.restoredItemIds.add(itemId)

          return {
            itemId: itemId,
            type: 'message',
            role: msg.role,
            status: 'completed',
            content: isUser
              ? [{ type: 'input_text', text: msg.transcript }]
              : [{ type: 'output_text', text: msg.transcript }]
          }
        })

        // Update the session history with previous messages
        this.session.updateHistory(historyItems)
      }

      // Switch to Session tab first
      this.activateSessionTab()

      // Start recording
      await this.recorder.begin()

      // Update ALL buttons with the button target (there may be multiple for mobile/desktop)
      const buttons = this.hasButtonTarget ? this.buttonTargets : [this.element]

      buttons.forEach((button) => {
        button.disabled = false
        button.innerHTML = '<i class="bi bi-stop-circle-fill"></i> Stop Session'
        button.classList.remove('btn-success', 'btn-primary')
        button.classList.add('btn-danger')
        button.dataset.action = 'click->voice-tutor#stopSession'
      })

      // Clear transcript to show listening indicator
      this.clearTranscript()

      // Start the recording loop (this continues until stopped)
      await this.recorder.record(async (data) => {
        await this.session.sendAudio(data.mono)
      })

      // Set up unload handlers to end session on tab close/navigation
      this.setupUnloadHandlers()
    } catch (error) {
      console.error('Failed to start session:', error)
      alert('Failed to start voice session: ' + error.message)

      // Reset button
      if (this.sessionButton) {
        this.sessionButton.disabled = false
        this.sessionButton.innerHTML = '<i class="bi bi-mic-fill"></i> Start Session'
      }
    }
  }

  setupEventListeners() {
    // Track which items we've already saved
    this.savedItemIds = new Set()

    // Track which items were restored from history (to avoid duplicating in UI)
    // This will be populated before setupEventListeners if resuming a session
    if (!this.restoredItemIds) {
      this.restoredItemIds = new Set()
    }

    // Initialize sequence counter based on existing messages in DOM
    const existingMessages = this.transcriptTarget?.querySelectorAll('.border-bottom') || []
    this.sequenceCounter = existingMessages.length

    // Listen for audio events - wavtools handles all the complexity
    this.session.on('audio', (event) => {
      this.player.add16BitPCM(event.data, event.responseId)
    })

    // Listen for audio interruption
    this.session.on('audio_interrupted', () => {
      this.player.interrupt()
    })

    // Listen for history updates - this is the proper SDK event
    this.session.on('history_updated', async (history) => {
      // Process each item in the history
      // savedItemIds Set provides O(1) deduplication
      for (const item of history) {
        // Skip if this was a restored item (already shown in server-rendered HTML)
        if (this.restoredItemIds?.has(item.itemId)) {
          continue
        }

        // Skip if we've already saved this item
        if (this.savedItemIds.has(item.itemId)) {
          continue
        }

        // Only save message items (not function calls)
        if (item.type === 'message' && item.role) {
          // Extract transcript from content
          let transcript = ''

          if (item.content && item.content.length > 0) {
            transcript = item.content
              .map((content) => {
                if (content.type === 'output_text' || content.type === 'input_text') {
                  return content.text
                }
                if (content.type === 'input_audio' || content.type === 'output_audio') {
                  return content.transcript || '[Audio only]'
                }
                return ''
              })
              .filter(t => t)
              .join('\n')
          }

          if (transcript && transcript !== '[Audio only]') {
            // Mark as saved immediately to prevent duplicates
            this.savedItemIds.add(item.itemId)

            // Display transcript in real-time
            this.displayTranscript(item.role, transcript)

            await this.saveMessage({
              role: item.role,
              transcript: transcript,
              message_type: item.content[0]?.type || 'audio',
              metadata: {
                item_id: item.itemId,
              },
            })
          }
        }
      }
    })

    // Listen for errors
    this.session.on('error', (error) => {
      console.error('Session error:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))
      if (error.error) {
        console.error('Inner error:', error.error)
      }
    })
  }

  async saveMessage(messageData) {
    try {
      const response = await fetch(
        `/dialogues/${this.conversationId}/save_message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector("[name='csrf-token']")
              .content,
          },
          body: JSON.stringify({
            role: messageData.role,
            transcript: messageData.transcript,
            message_type: messageData.message_type,
            metadata: messageData.metadata,
            sequence_number: this.sequenceCounter++,
          }),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Failed to save message:', response.status, errorText)
      }
    } catch (error) {
      console.error('Error saving message:', error)
    }
  }

  setupUnloadHandlers() {
    // Use multiple events for better reliability across browsers
    // Modern best practice: pagehide + visibilitychange + turbo:before-visit

    this.endSessionBeacon = () => {
      if (this.conversationId && !this.sessionEnded) {
        const token = document.querySelector("[name='csrf-token']")?.content
        const url = `/dialogues/${this.conversationId}/end_session?authenticity_token=${encodeURIComponent(token)}`

        const sent = navigator.sendBeacon(url, JSON.stringify({ ended_at: true }))

        if (sent) {
          this.sessionEnded = true
        } else {
          console.error('Failed to send session end beacon')
        }
      }
    }

    // pagehide: fires when page is hidden (recommended over unload)
    window.addEventListener('pagehide', this.endSessionBeacon)

    // visibilitychange: when tab becomes hidden
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.endSessionBeacon()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)

    // turbo:before-visit: for Turbo Drive navigation
    this.turboHandler = () => {
      this.endSessionBeacon()
    }
    document.addEventListener('turbo:before-visit', this.turboHandler)
  }

  removeUnloadHandlers() {
    if (this.endSessionBeacon) {
      window.removeEventListener('pagehide', this.endSessionBeacon)
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      document.removeEventListener('turbo:before-visit', this.turboHandler)
    }
  }

  async stopSession() {
    try {

      // Mark as ended to prevent duplicate beacon sends
      this.sessionEnded = true

      // Remove unload handlers since we're stopping normally
      this.removeUnloadHandlers()

      // Stop recorder
      if (this.recorder) {
        await this.recorder.end()
      }

      // Stop player
      if (this.player) {
        await this.player.interrupt()
      }

      // Capture usage statistics before closing session
      let totalTokens = null
      if (this.session) {
        const usage = this.session.usage
        totalTokens = usage.totalTokens

        await this.session.close()
        this.session = null
      }

      // Mark dialogue as ended
      const response = await fetch(`/dialogues/${this.conversationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          ended_at: true,
          total_tokens: totalTokens,
        }),
      })

      if (!response.ok) {
        console.error('Failed to mark conversation as ended:', response.status)
      }

      // Reload page to show ended state
      window.location.reload()
    } catch (error) {
      console.error('Error stopping session:', error)
      alert('Error stopping session: ' + error.message)
    }
  }

  activateSessionTab() {
    // Switch to the Session Transcript tab by clicking it
    const sessionTabButton = document.getElementById('session-tab-button')
    if (sessionTabButton) {
      sessionTabButton.click()
    }
  }

  clearTranscript() {
    // Add "listening" indicator without clearing existing messages
    if (this.hasTranscriptTarget) {
      // Update ALL transcript targets (mobile and desktop)
      this.transcriptTargets.forEach((transcriptTarget) => {
        // Check if there are existing messages (from server or previous session)
        const hasExistingMessages = transcriptTarget.querySelector('.border-bottom')

        if (hasExistingMessages) {
          // Don't clear - just add a listening indicator at the bottom
          const listeningDiv = document.createElement('div')
          listeningDiv.className = 'text-center text-muted py-4 listening-indicator'
          listeningDiv.innerHTML = `
            <div class="spinner-border spinner-border-sm mb-2" role="status">
              <span class="visually-hidden">Listening...</span>
            </div>
            <p class="small mb-0">Listening and transcribing...</p>
          `
          transcriptTarget.appendChild(listeningDiv)
        } else {
          // Fresh session - show centered message
          transcriptTarget.innerHTML = `
            <div class="text-center text-muted py-4">
              <div class="spinner-border mb-3" role="status">
                <span class="visually-hidden">Listening...</span>
              </div>
              <p>Listening and transcribing...</p>
            </div>
          `
        }
      })
    }
  }

  displayTranscript(role, text) {
    // Display a message in the transcript area
    if (!this.hasTranscriptTarget) return

    // Update ALL transcript targets (mobile and desktop)
    this.transcriptTargets.forEach((transcriptTarget, index) => {
      // Remove the listening indicator if present
      const listeningIndicator = transcriptTarget.querySelector('.listening-indicator')
      if (listeningIndicator) {
        listeningIndicator.remove()
      }

      // If this is a fresh session, clear the "no messages" placeholder
      if (transcriptTarget.querySelector('.spinner-border') && !transcriptTarget.querySelector('.border-bottom')) {
        transcriptTarget.innerHTML = ''
      }

      // Create message element
      const messageDiv = document.createElement('div')
      messageDiv.className = 'mb-3 pb-3 border-bottom'

      // Header with role label and version info
      const headerDiv = document.createElement('div')
      headerDiv.className = 'text-muted small mb-1 d-flex justify-content-between align-items-center'

      // Role label
      const roleLabel = document.createElement('span')
      roleLabel.className = 'text-uppercase fw-bold'
      roleLabel.textContent = role
      headerDiv.appendChild(roleLabel)

      // Add version info for assistant messages
      if (role === 'assistant') {
        const versionSpan = document.createElement('span')
        versionSpan.className = 'text-muted'
        versionSpan.textContent = `Lesson v${this.lessonVersionValue} • Instruction v${this.instructionVersionValue}`
        headerDiv.appendChild(versionSpan)
      }

      // Message text
      const textDiv = document.createElement('div')
      textDiv.className = 'text-break'
      textDiv.dataset.role = role
      textDiv.dataset.voiceTutorTarget = 'messageText'
      textDiv.textContent = text
      // Note: markdown transformation will happen via messageTextTargetConnected

      messageDiv.appendChild(headerDiv)
      messageDiv.appendChild(textDiv)
      transcriptTarget.appendChild(messageDiv)

      // Auto-scroll to bottom (only on the last target to avoid multiple scrolls)
      if (index === this.transcriptTargets.length - 1) {
        messageDiv.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    })
  }

  disconnect() {
    // Cleanup when Stimulus controller is disconnected

    // Remove event listeners
    this.removeUnloadHandlers()

    // Clean up resources
    if (this.recorder) {
      this.recorder.end()
    }
    if (this.player) {
      this.player.interrupt()
    }
    if (this.session) {
      this.session.close()
    }
  }
}
